import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Button, Form, Input, Modal, Select } from 'antd';
import { FormInstance, RequiredMark } from "antd/lib/form/Form";
import { useLazyQuery, useQuery } from "@apollo/client";
import useSpinner from "../../hooks/layout/useSpinner";
import { GET_AFF_BANK_ACCOUNT, GET_BANKS } from "./BankAccount.gql";
import { FormattedMessage, useIntl } from "react-intl";

interface Props {
    visible: boolean;
    setVisible: Dispatch<SetStateAction<boolean>>;
    editBankAccount: any;
    form: FormInstance<any>;
    selectedId: string;
}

const BankAccountEditModal = ({ visible, setVisible, editBankAccount, form, selectedId }: Props) => {
    const intl = useIntl();
    const { setLoading } = useSpinner();
    const [requiredMark, setRequiredMarkType] = useState<RequiredMark>('optional');
    const { data: bankData } = useQuery(GET_BANKS, { fetchPolicy: 'cache-and-network' });
    const onRequiredTypeChange = ({ requiredMarkValue }: { requiredMarkValue: RequiredMark }) => {
        setRequiredMarkType(requiredMarkValue);
    };
    const [getBankAccount, { data: getData, loading: getLoading }] = useLazyQuery(GET_AFF_BANK_ACCOUNT, { fetchPolicy: 'no-cache' });
    const [bankList, setBankList] = useState<any>();
    const [selectedBankAccount, setSelectedBankAccount] = useState<any>();

    useEffect(() => {
        if (!!selectedId && visible) {
            getBankAccount({ variables: { id: selectedId } });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId, visible]);

    const onClose = () => {
        setVisible(false);
    };

    useEffect(() => {
        if (getLoading)
            setLoading(true);
        else
            setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getLoading]);

    useEffect(() => {
        if (getData) {
            const account = getData && getData.affiliateBankAccount ? getData.affiliateBankAccount : null;
            if (account) {
                form.setFieldsValue({
                    accountName: account?.accountName,
                    accountNumber: account?.accountNumber,
                    bank: account?.bank?.id,
                    bankBranch: account?.bankBranch
                });
            }
            setSelectedBankAccount(account);
        } // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getData]);

    useEffect(() => {
        if (bankData) {
            const banks = bankData.banks && bankData.banks.length > 0 ?
                bankData.banks.map((val: any) => {
                    return {
                        ...val,
                        key: val.id
                    };
                }) : [];
            setBankList(banks);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bankData]);

    const onFinish = (fieldsValue: any) => {
        editBankAccount({
            variables: {
                affiliateBankAccountEditInput: {
                    id: selectedId,
                    accountName: fieldsValue?.accountName,
                    accountNumber: fieldsValue?.accountNumber,
                    bank: fieldsValue?.bank,
                    bankBranch: fieldsValue?.bankBranch
                },
            }
        });
    }

    return (
        <>
            <Modal
                title={<FormattedMessage id='EDIT_BANK_ACCOUNT' />}
                onCancel={onClose}
                open={visible}
                footer={null}
            >
                <div className="content-form">
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={onFinish}
                        onValuesChange={onRequiredTypeChange}
                        requiredMark={requiredMark}
                    >
                        {selectedId &&
                            <>
                                {selectedBankAccount?.withdrawMethod?.name === 'BANK_TRANSFER' &&
                                    <>
                                        <Form.Item label={<FormattedMessage id='BANK' />} name="bank">
                                            <Select style={{ width: 150 }} placeholder={intl.formatMessage({ id: 'BANK_HINT' })}>
                                                {
                                                    bankList?.map((d: any) => {
                                                        return (<Select.Option key={d.id} value={d.id}>{d.name}</Select.Option>)
                                                    })
                                                }
                                            </Select>
                                        </Form.Item>
                                        <Form.Item label={<FormattedMessage id='BANK_BRANCH' />} name="bankBranch" rules={[
                                            { required: true, message: <FormattedMessage id='BANK_BRANCH_REQUIRED' /> }
                                        ]}>
                                            <Input placeholder={intl.formatMessage({ id: 'BANK_BRANCH_HINT' })} />
                                        </Form.Item>
                                    </>
                                }

                                <Form.Item label={<FormattedMessage id='ACCOUNT_NAME' />} name="accountName" rules={[
                                    { required: true, message: <FormattedMessage id='ACCOUNT_NAME_REQUIRED' /> }
                                ]}>
                                    <Input placeholder={intl.formatMessage({ id: 'ACCOUNT_NAME_HINT' })} />
                                </Form.Item>
                                <Form.Item label={<FormattedMessage id='ACCOUNT_NUMBER' />} name="accountNumber"
                                    rules={[
                                        { required: true, message: <FormattedMessage id='ACCOUNT_NUMBER_REQUIRED' /> }
                                    ]}>
                                    <Input placeholder={intl.formatMessage({ id: 'ACCOUNT_NUMBER_HINT' })} />
                                </Form.Item>
                                <Form.Item className="form-button">
                                    <Button type="primary" htmlType="submit">
                                        {<FormattedMessage id='BTN_SAVE' />}
                                    </Button>
                                </Form.Item>
                            </>
                        }
                    </Form>
                </div>
            </Modal>
        </>
    );
};

export default BankAccountEditModal;
